import type { MediaAsset, ResolvedAssets } from '@/types/api/assets';

/** Marks a Blobstore share link: `/view/<assetId>?source=blobstore`. */
export const SHARE_LINK_SOURCE_PARAM = 'source';
export const BLOBSTORE_SHARE_SOURCE = 'blobstore';
/** Rides on a Blobstore link, with `type` and `extension`, so the viewer can fall back to Cloudinary. */
export const SHARE_LINK_PUBLIC_ID_PARAM = 'publicId';

/**
 * The link "copy link" hands out. A resolved Blobstore asset gets a Blobstore link, carrying its Cloudinary
 * fields when it has them so the viewer can fall back; any other asset gets its Cloudinary link.
 * A resolved entry IS the read flag: backend only populates resolvedAssets under blobstore-read for embed users.
 */
export function shareLinkUrl(file: MediaAsset, resolved: ResolvedAssets): string {
  const assetId = file?.blobstore?.id;
  const cloudinary = cloudinaryLinkFields(file);

  if (!assetId || !resolved[assetId]) {
    return cloudinary
      ? `${window.location.origin}/view/${cloudinary.publicId}?type=${cloudinary.type}&extension=${cloudinary.extension}`
      : '';
  }
  if (!cloudinary) {
    return blobstoreShareLink(assetId);
  }

  const params = new URLSearchParams({
    [SHARE_LINK_SOURCE_PARAM]: BLOBSTORE_SHARE_SOURCE,
    [SHARE_LINK_PUBLIC_ID_PARAM]: cloudinary.publicId,
    type: cloudinary.type,
    extension: cloudinary.extension,
  });
  return `${window.location.origin}/view/${assetId}?${params}`;
}

const cloudinaryLinkFields = (file: MediaAsset) => {
  if (!file?.url || !file?.publicId) {
    return null;
  }
  return {
    publicId: file.publicId.replace('uploads/', ''),
    type: file.type,
    extension: file.url.split('.').pop() ?? '',
  };
};

export const blobstoreShareLink = (assetId: string) =>
  `${window.location.origin}/view/${assetId}?${SHARE_LINK_SOURCE_PARAM}=${BLOBSTORE_SHARE_SOURCE}`;

const SAFE_EXTENSION = /^[a-z0-9]+$/i;
/** Path segments of word characters and dashes only, so a query-supplied id cannot climb out of `uploads/`. */
const SAFE_PUBLIC_ID = /^[\w-]+(\/[\w-]+)*$/;

export const cloudinaryMediaUrl = (publicId: string, extension: string | null, type: string) => {
  // Dot segments survive in a query param and normalise once concatenated into the path.
  const safeExtension = extension && SAFE_EXTENSION.test(extension) ? extension : '';
  return `https://media.weavy.ai/${type}/upload/uploads/${publicId}.${safeExtension}`;
};

/** Cloudinary serves audio from its video pipeline; everything else, 3D included, from image. */
export const cloudinaryDeliveryType = (type: string | null) =>
  type === 'video' || type === 'audio' ? 'video' : 'image';

/** The Cloudinary URL a Blobstore link falls back to, or null when it carries no usable one. */
export function cloudinaryFallbackUrl(search: URLSearchParams): string | null {
  const publicId = search.get(SHARE_LINK_PUBLIC_ID_PARAM);
  if (!publicId || !SAFE_PUBLIC_ID.test(publicId)) {
    return null;
  }
  const type = search.get('type');
  return cloudinaryMediaUrl(publicId, search.get('extension'), cloudinaryDeliveryType(type));
}
